import { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@/components/Modal";
import { buttonVariants } from "@markeroo/theme";
import { ui } from "@/strings";

interface ArchiveButtonProps {
  onClick: () => void;
  confirmMessage?: string;
  loading?: boolean;
  disabled?: boolean;
  isArchived?: boolean;
}

/**
 * ArchiveButton provides a styled button for archive/unarchive actions.
 * Optionally shows a confirmation modal before executing the action.
 * Automatically stops event propagation to prevent triggering parent click handlers.
 * Shows "Unarchive" text when isArchived is true.
 */
export function ArchiveButton({
  onClick,
  confirmMessage,
  loading = false,
  disabled = false,
  isArchived = false,
}: ArchiveButtonProps) {
  const [isConfirmOpen, setConfirmOpen] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirmMessage) {
      setConfirmOpen(true);
    } else {
      onClick();
    }
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    onClick();
  };

  const ariaLabel = () => {
    if (loading) {
      return isArchived ? ui.actions.unarchiving : ui.actions.archiving;
    }
    return isArchived ? ui.actions.unarchive : ui.actions.archive;
  };

  // When isArchived=true, show a proper "Restore" button instead of X
  if (isArchived) {
    return (
      <>
        <button
          type="button"
          className={`${buttonVariants.secondary} w-full sm:w-auto`}
          onClick={handleClick}
          disabled={loading || disabled}
          aria-label={ariaLabel()}
        >
          {loading ? ui.actions.unarchiving : ui.actions.unarchive}
        </button>

        {confirmMessage && (
          <Modal isOpen={isConfirmOpen} onClose={() => setConfirmOpen(false)}>
            <ModalHeader
              title={`${ui.actions.confirm} ${ui.actions.unarchive}`}
              onClose={() => setConfirmOpen(false)}
            />
            <ModalBody>
              <p>{confirmMessage}</p>
            </ModalBody>
            <ModalFooter>
              <button
                className={buttonVariants.secondary}
                onClick={() => setConfirmOpen(false)}
              >
                {ui.actions.cancel}
              </button>
              <button className={buttonVariants.danger} onClick={handleConfirm}>
                {ui.actions.unarchive}
              </button>
            </ModalFooter>
          </Modal>
        )}
      </>
    );
  }

  // When not archived, show the X button for archiving
  return (
    <>
      <button
        type="button"
        className="text-theme-muted hover:text-theme-secondary text-xl leading-none p-0 w-6 h-6 flex items-center justify-center"
        onClick={handleClick}
        disabled={loading || disabled}
        aria-label={ariaLabel()}
        title={ariaLabel()}
      >
        {/* eslint-disable-next-line no-restricted-syntax */}
        <span aria-hidden="true">✕</span>
      </button>

      {confirmMessage && (
        <Modal isOpen={isConfirmOpen} onClose={() => setConfirmOpen(false)}>
          <ModalHeader
            title={`${ui.actions.confirm} ${ui.actions.archive}`}
            onClose={() => setConfirmOpen(false)}
          />
          <ModalBody>
            <p>{confirmMessage}</p>
          </ModalBody>
          <ModalFooter>
            <button
              className={buttonVariants.secondary}
              onClick={() => setConfirmOpen(false)}
            >
              {ui.actions.cancel}
            </button>
            <button className={buttonVariants.danger} onClick={handleConfirm}>
              {ui.actions.archive}
            </button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
}
